import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Avatar, Box, Button, Flex, HStack, Link, Skeleton, SkeletonCircle, Text, VStack } from '@bonusly/design-system';
import type { FetchHttpError } from '@bonusly/http-client';

import { useGithubAuthorize, useGithubCollaborators, useGithubDisconnect, useGithubStatus } from '../../queries/github';
import { GithubCollaborator, GithubCollaboratorPullRequest } from '../../schemas/github';
import { BizySettingsSecondaryView } from '../bizy-settings/bizy-settings-secondary-view';
import { GithubLogo } from './github-logo';
import { GithubScopePicker } from './github-scope-picker';

interface GithubManagementViewProps {
	onBack: () => void;
}

const getErrorStatus = (error: unknown): number | undefined => {
	const httpError = error as FetchHttpError | undefined;
	return httpError?.status ?? httpError?.response?.status;
};

const getErrorCode = (error: unknown): string | undefined => {
	const httpError = error as FetchHttpError | undefined;
	const data = httpError?.response?.data;
	return typeof data === 'object' && data !== null && 'error' in data ? String(data.error) : undefined;
};

const getReferenceLabel = (url: string, pullRequestsByUrl: Map<string, GithubCollaboratorPullRequest>, fallbackLabel: string) => {
	const pullRequest = pullRequestsByUrl.get(url);
	if (!pullRequest) return fallbackLabel;
	return `${pullRequest.repo}#${pullRequest.number}`;
};

export const GithubManagementView: FC<GithubManagementViewProps> = ({ onBack }) => {
	const { t } = useTranslation('bizy_settings', { keyPrefix: 'github' });
	const { data: status, isLoading: isLoadingStatus } = useGithubStatus();
	const { mutateAsync: disconnect, isLoading: isDisconnecting } = useGithubDisconnect();
	const { mutateAsync: authorize, isLoading: isAuthorizing } = useGithubAuthorize();
	const { track } = useAnalytics();

	const selectedOrgs = status?.selectedOrgs ?? [];
	const selectedRepos = status?.selectedRepos ?? [];
	const hasSelectedAccess = selectedOrgs.length > 0 || selectedRepos.length > 0;
	const isConnected = status?.status === 'connected';
	const collaborators = useGithubCollaborators(isConnected && hasSelectedAccess);
	const collaboratorsNotConnected = getErrorStatus(collaborators.error) === 401 || getErrorCode(collaborators.error) === 'not_connected';

	const handleDisconnect = useCallback(async () => {
		try {
			await disconnect();
			track(AnalyticsEvent.Github.Disconnected);
			onBack();
		} catch {
			// Error handled by React Query
		}
	}, [disconnect, onBack, track]);

	const handleConnect = useCallback(async () => {
		track(AnalyticsEvent.Github.ConnectClicked);
		try {
			const { redirectUrl } = await authorize(window.location.pathname);
			if (redirectUrl) {
				sessionStorage.setItem('bizy_connecting_github', 'true');
				window.location.href = redirectUrl;
			}
		} catch {
			// Error handled by React Query
		}
	}, [authorize, track]);

	if (isLoadingStatus) return null;

	return (
		<BizySettingsSecondaryView>
			<Box>
				<Flex align={{ base: 'stretch', md: 'flex-start' }} direction={{ base: 'column', md: 'row' }} gap={5} justify="space-between">
					<HStack align="flex-start" spacing={4}>
						<Box color="text.default" flexShrink={0} display="inline-flex" alignItems="center" mt={1}>
							<GithubLogo width="32" height="32" />
						</Box>
						<Box>
							<Text size="lg" variant="strong" color="text.default" mb={1}>
								{t('management.title')}
							</Text>
							<Text color="text.softer" size="sm" maxW="560px">
								{t('management.description')}
							</Text>
						</Box>
					</HStack>
					<Button
						colorScheme="danger"
						isLoading={isDisconnecting}
						onClick={handleDisconnect}
						size="sm"
						variant="ghost"
						alignSelf={{ base: 'flex-start', md: 'center' }}
					>
						{t('management.disconnect')}
					</Button>
				</Flex>
			</Box>

			{isConnected && <GithubScopePicker selectedOrgs={selectedOrgs} selectedRepos={selectedRepos} />}

			{isConnected && (
				<CodeReviewCollaboratorsPanel
					collaborators={collaborators.data?.collaborators ?? []}
					error={collaborators.error}
					hasSelectedAccess={hasSelectedAccess}
					isError={collaborators.isError}
					isFetching={collaborators.isFetching}
					isLoading={collaborators.isLoading}
					isNotConnected={collaboratorsNotConnected}
					isAuthorizing={isAuthorizing}
					onConnect={handleConnect}
					onRefresh={() => collaborators.refetch()}
					pullRequests={collaborators.data?.pullRequests ?? []}
				/>
			)}
		</BizySettingsSecondaryView>
	);
};

interface CodeReviewCollaboratorsPanelProps {
	collaborators: GithubCollaborator[];
	error: unknown;
	hasSelectedAccess: boolean;
	isAuthorizing: boolean;
	isError: boolean;
	isFetching: boolean;
	isLoading: boolean;
	isNotConnected: boolean;
	onConnect: () => void;
	onRefresh: () => void;
	pullRequests: GithubCollaboratorPullRequest[];
}

const CodeReviewCollaboratorsPanel: FC<CodeReviewCollaboratorsPanelProps> = ({
	collaborators,
	hasSelectedAccess,
	isAuthorizing,
	isError,
	isFetching,
	isLoading,
	isNotConnected,
	onConnect,
	onRefresh,
	pullRequests,
}) => {
	const { t } = useTranslation('bizy_settings', { keyPrefix: 'github.collaborators' });
	const pullRequestsByUrl = new Map(pullRequests.map((pullRequest) => [pullRequest.url, pullRequest]));

	const renderBody = () => {
		if (isNotConnected) {
			return (
				<CollaboratorsEmptyState
					title={t('notConnectedTitle')}
					description={t('notConnectedDescription')}
					action={
						<Button colorScheme="primary" isLoading={isAuthorizing} onClick={onConnect} size="sm">
							{t('connect')}
						</Button>
					}
				/>
			);
		}

		if (!hasSelectedAccess) {
			return <CollaboratorsEmptyState title={t('selectReposTitle')} description={t('selectReposDescription')} />;
		}

		if (isLoading) return <CollaboratorsLoadingState />;

		if (isError) {
			return (
				<CollaboratorsEmptyState
					title={t('errorTitle')}
					description={t('errorDescription')}
					action={
						<Button colorScheme="secondary" isLoading={isFetching} onClick={onRefresh} size="sm" variant="outline">
							{t('retry')}
						</Button>
					}
				/>
			);
		}

		if (collaborators.length === 0) {
			return <CollaboratorsEmptyState title={t('emptyTitle')} description={t('emptyDescription')} />;
		}

		return (
			<VStack align="stretch" spacing={0}>
				{collaborators.map((collaborator) => (
					<CollaboratorRow key={collaborator.githubLogin} collaborator={collaborator} pullRequestsByUrl={pullRequestsByUrl} />
				))}
			</VStack>
		);
	};

	return (
		<Box bg="bg.canvas" borderRadius="lg" p={4}>
			<Flex align={{ base: 'stretch', md: 'flex-start' }} direction={{ base: 'column', md: 'row' }} gap={3} justify="space-between" mb={3}>
				<Box>
					<Text color="text.default" size="md" variant="strong">
						{t('title')}
					</Text>
					<Text color="text.softer" size="sm">
						{t('description')}
					</Text>
				</Box>
				<Button
					alignSelf={{ base: 'flex-start', md: 'center' }}
					colorScheme="secondary"
					isLoading={isFetching && !isLoading}
					onClick={onRefresh}
					size="sm"
					variant="ghost"
				>
					{t('refresh')}
				</Button>
			</Flex>
			{renderBody()}
		</Box>
	);
};

const CollaboratorsLoadingState: FC = () => {
	const { t } = useTranslation('bizy_settings', { keyPrefix: 'github.collaborators' });

	return (
		<VStack align="stretch" spacing={3}>
			<Text color="text.softer" size="sm">
				{t('loading')}
			</Text>
			{[0, 1, 2].map((index) => (
				<HStack key={index} spacing={3}>
					<SkeletonCircle size="10" />
					<Box flex={1}>
						<Skeleton borderRadius="md" h={4} mb={2} w="32%" />
						<Skeleton borderRadius="md" h={3} w="70%" />
					</Box>
				</HStack>
			))}
		</VStack>
	);
};

interface CollaboratorsEmptyStateProps {
	action?: JSX.Element;
	description: string;
	title: string;
}

const CollaboratorsEmptyState: FC<CollaboratorsEmptyStateProps> = ({ action, description, title }) => (
	<Box bg="bg.surface" border="1px solid" borderColor="border.soft" borderRadius="md" p={3}>
		<Text color="text.default" size="sm" variant="strong">
			{title}
		</Text>
		<Text color="text.softer" mt={1} size="sm">
			{description}
		</Text>
		{action && <Box mt={3}>{action}</Box>}
	</Box>
);

interface CollaboratorRowProps {
	collaborator: GithubCollaborator;
	pullRequestsByUrl: Map<string, GithubCollaboratorPullRequest>;
}

const CollaboratorRow: FC<CollaboratorRowProps> = ({ collaborator, pullRequestsByUrl }) => {
	const { t } = useTranslation('bizy_settings', { keyPrefix: 'github.collaborators' });
	const profileUrl = `https://github.com/${collaborator.githubLogin}`;
	const avatarUrl = `${profileUrl}.png`;
	const summary = getCollaboratorSummary(collaborator, t);
	const references = collaborator.references.slice(0, 3);

	return (
		<Box borderTop="1px solid" borderColor="border.soft" py={3}>
			<HStack align="flex-start" spacing={3}>
				<Avatar name={collaborator.githubLogin} src={avatarUrl} size="sm" />
				<Box minW={0} flex={1}>
					<Link color="text.interactive" fontWeight="semibold" href={profileUrl} isExternal>
						@{collaborator.githubLogin}
					</Link>
					<Text color="text.softer" size="sm">
						{summary}
					</Text>
					{references.length > 0 && (
						<HStack flexWrap="wrap" mt={2} spacing={2}>
							{references.map((reference, index) => (
								<Link key={reference.referenceUrl} color="text.interactive" href={reference.referenceUrl} isExternal size="sm">
									{getReferenceLabel(reference.referenceUrl, pullRequestsByUrl, t('reference', { count: index + 1 }))}
								</Link>
							))}
						</HStack>
					)}
				</Box>
			</HStack>
		</Box>
	);
};

const getCollaboratorSummary = (collaborator: GithubCollaborator, t: ReturnType<typeof useTranslation>['t']) => {
	const signals = [
		collaborator.reviewedPullRequestsCount > 0 ? t('signals.reviewed', { count: collaborator.reviewedPullRequestsCount }) : null,
		collaborator.commentedPullRequestsCount > 0 ? t('signals.commented', { count: collaborator.commentedPullRequestsCount }) : null,
		collaborator.authoredPullRequestsCount > 0 ? t('signals.authored', { count: collaborator.authoredPullRequestsCount }) : null,
	].filter(Boolean);

	return signals.slice(0, 2).join(` ${t('signals.separator')} `) || t('signals.fallback');
};
